import { defineComponent, inject, computed, onMounted, ref, onBeforeMount, onUnmounted, getCurrentInstance, watch } from 'vue';
import { getFallback, getTranslateProps } from '@tolgee/web';
export * from '@tolgee/web';

/* eslint-disable @typescript-eslint/no-var-requires */
const TolgeeProvider = defineComponent({
    name: 'TolgeeProvider',
    props: {
        tolgee: { type: Object, required: false },
        fallback: {
            type: [Object, String],
        },
        staticData: {
            type: Object,
            required: false,
        },
        language: {
            type: String,
            required: false,
        },
    },
    setup(props) {
        const tolgeeContext = inject('tolgeeContext');
        // for backward compatibility
        if (props.tolgee) {
            tolgeeContext.value.tolgee = props.tolgee;
        }
        const tolgee = computed(() => tolgeeContext.value.tolgee);
        if (!tolgee.value) {
            throw new Error('Tolgee instance not provided');
        }
        if (tolgeeContext.value.isInitialRender) {
            if (!props.staticData || !props.language) {
                throw new Error('TolgeeProvider: "staticData" and "language" props are required for SSR.');
            }
            tolgee.value.setEmitterActive(false);
            tolgee.value.addStaticData(props.staticData);
            tolgee.value.changeLanguage(props.language);
            tolgee.value.setEmitterActive(true);
            if (!tolgee.value.isLoaded()) {
                // warning user, that static data provided are not sufficient
                // for proper SSR render
                const missingRecords = tolgee.value
                    .getRequiredRecords(props.language)
                    .map(({ namespace, language }) => namespace ? `${namespace}:${language}` : language)
                    .filter((key) => { var _a; return !((_a = props.staticData) === null || _a === void 0 ? void 0 : _a[key]); });
                // eslint-disable-next-line no-console
                console.warn(`Tolgee: Missing records in "staticData" for proper SSR functionality: ${missingRecords.map((key) => `"${key}"`).join(', ')}`);
            }
        }
        onMounted(() => {
            tolgeeContext.value.isInitialRender = false;
        });
        const isLoading = ref(!tolgee.value.isLoaded());
        onBeforeMount(() => {
            tolgee.value.run().finally(() => {
                isLoading.value = false;
            });
        });
        onUnmounted(() => {
            tolgee.value.stop();
        });
        return { isLoading };
    },
    render() {
        var _a, _b, _c, _d;
        return !this.isLoading
            ? (_b = (_a = this.$slots).default) === null || _b === void 0 ? void 0 : _b.call(_a)
            : ((_d = (_c = this.$slots).fallback) === null || _d === void 0 ? void 0 : _d.call(_c)) || this.fallback || null;
    },
});

const useTranslateInternal = (ns) => {
    const namespaces = getFallback(ns);
    const tolgeeContext = inject('tolgeeContext');
    const tolgee = computed(() => tolgeeContext.value.tolgee);
    if (!tolgee.value) {
        throw new Error('Tolgee instance not provided');
    }
    const t = ref(createTFunction());
    const subscription = tolgee.value.onNsUpdate(() => {
        t.value = createTFunction();
        isLoading.value = !tolgee.value.isLoaded(namespaces);
    });
    subscription.subscribeNs(namespaces);
    tolgee.value.addActiveNs(namespaces);
    onUnmounted(() => {
        subscription === null || subscription === void 0 ? void 0 : subscription.unsubscribe();
        tolgee.value.removeActiveNs(namespaces);
    });
    const isLoading = ref(!tolgee.value.isLoaded(namespaces));
    const subscribeToNs = (ns) => {
        subscription.subscribeNs(ns);
    };
    function createTFunction() {
        return (props) => {
            var _a;
            const fallbackNs = (_a = props.ns) !== null && _a !== void 0 ? _a : namespaces === null || namespaces === void 0 ? void 0 : namespaces[0];
            subscribeToNs(fallbackNs);
            return tolgee.value.t(Object.assign(Object.assign({}, props), { ns: fallbackNs }));
        };
    }
    return { t, isLoading };
};

const T = defineComponent({
    name: 'T',
    props: {
        keyName: { type: String, required: true },
        params: Object,
        defaultValue: String,
        noWrap: {
            type: Boolean,
            default: false,
        },
        ns: { type: String },
        language: { type: String },
    },
    setup() {
        const { t } = useTranslateInternal();
        return { t };
    },
    render() {
        const slotsParams = {};
        Object.keys(this.$slots).forEach((key) => {
            slotsParams[key] = this.$slots[key]();
        });
        const assignedParams = Object.assign({}, this.$props.params, slotsParams);
        const params = {
            key: this.$props.keyName,
            params: assignedParams,
            defaultValue: this.$props.defaultValue,
            noWrap: this.$props.noWrap,
            ns: this.$props.ns,
            language: this.$props.language,
        };
        const content = this.t(params);
        return content;
    },
});

const useTolgee = (events) => {
    const instance = getCurrentInstance();
    const tolgeeContext = inject('tolgeeContext');
    const tolgee = computed(() => tolgeeContext.value.tolgee);
    const listeners = events === null || events === void 0 ? void 0 : events.map((e) => {
        return tolgee.value.on(e, () => {
            tolgeeContext.value.tolgee = Object.freeze(Object.assign({}, tolgee.value));
            instance.proxy.$forceUpdate();
        });
    });
    onUnmounted(() => {
        listeners === null || listeners === void 0 ? void 0 : listeners.forEach((listener) => listener.unsubscribe());
    });
    return tolgee;
};

const useTranslate = (namespaces) => {
    const { t: tInternal, isLoading } = useTranslateInternal(namespaces);
    const t = computed(() => (...params) => {
        // @ts-ignore
        const props = getTranslateProps(...params);
        return tInternal.value(props);
    });
    return { t, isLoading };
};

const GlobalContextPlugin = () => (tolgee) => {
    return tolgee;
};

const VueTolgee = {
    install(app, options) {
        const tolgee = options === null || options === void 0 ? void 0 : options.tolgee;
        if (!tolgee) {
            throw new Error('Tolgee instance not passed in options');
        }
        const isSsrEnabled = Boolean(options === null || options === void 0 ? void 0 : options.enableSSR);
        const reactiveContext = ref({
            tolgee: tolgee,
            isInitialRender: isSsrEnabled,
        });
        app.provide('tolgeeContext', reactiveContext);
        if (isSsrEnabled) {
            const getOriginalTolgeeInstance = () => (Object.assign(Object.assign({}, reactiveContext.value.tolgee), { t: ((...args) => {
                    const props = getTranslateProps(...args);
                    return tolgee.t(Object.assign({}, props));
                }) }));
            const getTolgeeInstanceWithDeactivatedWrapper = () => (Object.assign(Object.assign({}, reactiveContext.value.tolgee), { t: ((...args) => {
                    const props = getTranslateProps(...args);
                    return tolgee.t(Object.assign(Object.assign({}, props), { noWrap: true }));
                }) }));
            reactiveContext.value.tolgee = getTolgeeInstanceWithDeactivatedWrapper();
            watch(() => reactiveContext.value.isInitialRender, (isInitialRender) => {
                if (!isInitialRender) {
                    reactiveContext.value.tolgee = getOriginalTolgeeInstance();
                }
            });
        }
        reactiveContext.value.tolgee.on('cache', () => {
            reactiveContext.value.tolgee = Object.freeze(Object.assign({}, reactiveContext.value.tolgee));
        });
        app.config.globalProperties.$t = ((...args) => reactiveContext.value.tolgee.t(...args));
        // keep it for backward compatibility
        // but it is not reactive
        // not recommended to use it
        app.config.globalProperties.$tolgee = reactiveContext.value.tolgee;
    },
};

export { GlobalContextPlugin, T, TolgeeProvider, VueTolgee, useTolgee, useTranslate };
//# sourceMappingURL=tolgee-vue.esm.js.map
